import { api, withFreshAccess } from './client'
import { useAccounts } from '../store/accounts'
import { upsertProfile } from '../cache/db'

interface ProfileResp {
  did: string
  handle: string
  displayName?: string
  avatar?: string
  description?: string
  banner?: string
  followsCount?: number
  followersCount?: number
  postsCount?: number
}

/**
 * Refresh displayName + avatar for one account by hitting
 * app.bsky.actor.getProfile through the worker proxy. Errors are swallowed
 * — a stale avatar is harmless.
 */
export async function hydrateAccount(accountId: string): Promise<void> {
  const acc = useAccounts.getState().accounts.find((a) => a.id === accountId)
  if (!acc) return
  try {
    const r = await withFreshAccess(acc.id, () =>
      api.xrpc<ProfileResp>({
        accountId: acc.id,
        method: 'GET',
        path: `app.bsky.actor.getProfile?actor=${encodeURIComponent(acc.did)}`,
      }),
    )
    useAccounts.getState().updateProfile(acc.id, {
      handle: r.handle ?? acc.handle,
      displayName: r.displayName ?? null,
      avatar: r.avatar ?? null,
    })
    // Persist to IndexedDB too so a later deactivation / 429 / offline
    // session has something to render in ProfileTab. The zustand store
    // alone only kept handle/displayName/avatar; db.profiles holds the
    // full profile (description, banner, counts) too.
    await upsertProfile({
      did: r.did ?? acc.did,
      handle: r.handle ?? acc.handle,
      uri: `at://${r.did ?? acc.did}`,
      profile: r as unknown as Record<string, unknown>,
      fetched_at: Date.now(),
    })
  } catch (e) {
    // ignore — keep whatever we already had. Log the full DID (handles can
    // change; the DID can't) so a failed refresh stays traceable to the
    // account even after a rename.
    console.warn(`[hydrateAccount] getProfile failed for ${acc.did}`, e)
  }
}

/**
 * Hydrate every account on app boot. Sequential to avoid hammering the
 * proxy if a user has 10+ accounts. Each call is best-effort.
 */
export async function hydrateAllAccounts(): Promise<void> {
  const ids = useAccounts.getState().accounts.map((a) => a.id)
  for (const id of ids) {
    await hydrateAccount(id)
  }
}
